import { useCallback, useEffect, useState } from 'react'
import Layout from '../../components/Layout'
import {
  getSportsBetList,
  getVoidAudit,
  voidSingleOpenBet,
  deleteSportsBet,
  voidCasinoBets,
  voidSettledCasinoBet,
  deleteCasinoBet
} from '../../services/api'
import SettlementVoidModal from '../../components/SettlementVoidModal'

/**
 * Total Bets (owner only) — saari SPORTS bets ek jagah, do tab me:
 *
 *   Unsettled → /d99/bet-list?type=current  (open/matched, result pending)
 *   Settled   → /d99/bet-list?type=past     (won/lost/void — result aa chuka)
 *
 * Dono server se created_at DESC aati hain aur server hi paginate karta hai
 * (page/limit), yahan sirf page number bhejte hain.
 *
 * Status:
 *   unsettled → OPEN
 *   settled   → WIN / LOSS, ya VOID agar ye bet void-audit me milti hai
 *               (bet-list ka past branch void ko alag nahi batata, isliye
 *               void-audit se bet ids uthate hain). Deleted bets purge ho
 *               jaati hain, wo list me aati hi nahi.
 *
 * Action sirf tab dikhta hai jab bet pehle se void/deleted NA ho:
 *   unsettled → Void Bet (void-single-bet) / Delete Bet (delete-bet)
 *   settled   → Delete Bet (delete-bet, bet_id se — server khud settlement row
 *               dhoondhta hai; 24h window server par hai)
 * Backend me koi change nahi — sab existing APIs.
 */
const LIMIT = 50

const fmt = (v) => Number(v || 0).toFixed(2)

const fmtDate = (v) => {
  if (!v) return '-'
  const d = new Date(v)
  const p = (n) => String(n).padStart(2, '0')
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
}

const TABS = [
  { key: 'unsettled', label: 'Unsettled Bets' },
  { key: 'settled', label: 'Settled Bets' }
]

// Sports aur Casino dono isi page par — same bet-list API, bas category alag.
// Casino actions:
//   unsettled → Void  /void-casino-bets        { bet_ids: [id] }
//   settled   → Void  /void-settled-casino-bet { bet_id }
//   dono      → Delete /delete-casino-bet     { bet_id }  (void + purge)
// Casino ka settled branch result_status me 'void'/'refunded' seedha bhejta hai,
// isliye wahan void-audit ki zaroorat nahi.
const CATEGORIES = [
  { key: 'sport', label: 'Sports' },
  { key: 'casino', label: 'Casino' }
]
const CASINO_LOCKED = ['VOID', 'VOIDED', 'REFUNDED', 'REFUND', 'CANCELLED', 'DELETED']

export default function TotalBets() {
  const [category, setCategory] = useState('sport')
  const [tab, setTab] = useState('unsettled')
  const [bets, setBets] = useState([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState(null)

  // Void audit se bet ids — settled tab me VOID status dikhane ke liye
  const [voidedIds, setVoidedIds] = useState(() => new Set())

  const [confirm, setConfirm] = useState(null) // { type: 'void'|'delete', bet }
  const [txnPassword, setTxnPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [actionError, setActionError] = useState('')

  const fetchVoided = useCallback(async () => {
    try {
      // Sirf sports, bet ids chahiye — 500 server ka max hai
      const resp = await getVoidAudit({ bet_kind: 'SPORTS', limit: 500, page: 1 })
      if (resp?.success && Array.isArray(resp.data)) {
        setVoidedIds(new Set(resp.data.map((r) => String(r.bet_id))))
      }
    } catch {
      // best-effort — na mile to VOID badge nahi dikhega, action fir bhi server rokta hai
    }
  }, [])

  const fetchBets = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const resp = await getSportsBetList({
        type: tab === 'settled' ? 'past' : 'current',
        category,
        page,
        limit: LIMIT,
        // Server search sirf past (settled) branch me hai — match/market/selection par.
        // Unsettled me neeche client-side filter lagta hai (is page ki rows par).
        search: tab === 'settled' && query ? query : undefined
      })
      if (resp?.success) {
        setBets(Array.isArray(resp.data) ? resp.data : [])
        setTotal(Number(resp.pagination?.total || 0))
        setTotalPages(Number(resp.pagination?.totalPages || 0))
      } else {
        setBets([])
        setTotal(0)
        setTotalPages(0)
        setError(resp?.message || resp?.error || 'Failed to load')
      }
    } catch (err) {
      setBets([])
      setError(err?.response?.data?.message || err?.response?.data?.error || err.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [category, tab, page, query])

  useEffect(() => { fetchBets() }, [fetchBets])
  useEffect(() => { fetchVoided() }, [fetchVoided])

  const switchTab = (key) => {
    if (key === tab) return
    setTab(key)
    setPage(1)
    setNotice(null)
  }

  const switchCategory = (key) => {
    if (key === category) return
    setCategory(key)
    setTab('unsettled')
    setPage(1)
    setNotice(null)
    setSearch('')
    setQuery('')
  }

  const isCasino = category === 'casino'

  const runSearch = () => { setPage(1); setQuery(search.trim()) }

  const isVoided = (b) => {
    if (isCasino) {
      // casino: settled branch result_status khud bata deta hai
      return tab === 'settled' && CASINO_LOCKED.includes(String(b.result_status || '').toUpperCase())
    }
    return voidedIds.has(String(b.id))
  }

  // Unsettled tab: server search nahi hai, isliye loaded page ki rows par
  // username / match / selection se filter. Settled tab me server filter karta hai.
  const q = query.toLowerCase()
  const visibleBets = tab === 'unsettled' && q
    ? bets.filter((b) =>
        String(b.username || '').toLowerCase().includes(q) ||
        String(b.description || '').toLowerCase().includes(q) ||
        String(b.selection || '').toLowerCase().includes(q))
    : bets

  const statusOf = (b) => {
    if (isVoided(b)) return { label: 'VOID', cls: 'bg-secondary' }
    if (tab === 'unsettled') return { label: 'OPEN', cls: 'bg-primary' }
    const r = String(b.result_status || '').toUpperCase()
    if (r === 'WIN') return { label: 'WIN', cls: 'bg-success' }
    if (r === 'LOSS') return { label: 'LOSS', cls: 'bg-danger' }
    return { label: r || 'SETTLED', cls: 'bg-dark' }
  }

  const openConfirm = (type, bet) => {
    setTxnPassword('')
    setActionError('')
    setConfirm({ type, bet })
  }

  const doAction = async () => {
    if (!confirm) return
    setSubmitting(true)
    setActionError('')
    try {
      const id = confirm.bet.id
      let resp
      if (isCasino) {
        if (confirm.type === 'delete') {
          resp = await deleteCasinoBet({ bet_id: id, transactionPassword: txnPassword })
        } else {
          resp = tab === 'settled'
            ? await voidSettledCasinoBet({ bet_id: id, transactionPassword: txnPassword })
            : await voidCasinoBets({ bet_ids: [id], transactionPassword: txnPassword })
        }
      } else {
        const payload = { bet_id: id, transactionPassword: txnPassword }
        resp = confirm.type === 'void'
          ? await voidSingleOpenBet(payload)
          : await deleteSportsBet(payload)
      }

      if (resp?.success) {
        setNotice({
          ok: true,
          message: confirm.type === 'void'
            ? `Bet #${confirm.bet.id} voided for ${confirm.bet.username}`
            : `Bet #${confirm.bet.id} deleted for ${confirm.bet.username}`
        })
        setConfirm(null)
        fetchBets()
        fetchVoided()
      } else {
        setActionError(resp?.error || resp?.message || 'Unknown error')
      }
    } catch (err) {
      setActionError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Request failed')
    } finally {
      setSubmitting(false)
    }
  }

  const from = total === 0 ? 0 : (page - 1) * LIMIT + 1
  const to = Math.min(page * LIMIT, total)

  // Page buttons: current ke aas-paas 2, aur first/last — lambi list me bhi chhota rahe
  const pageNumbers = () => {
    const nums = new Set([1, totalPages, page - 2, page - 1, page, page + 1, page + 2])
    return [...nums].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b)
  }

  return (
    <Layout title="Total Bets - Diamond Admin">
      <div className="listing-grid">
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-flex align-items-center justify-content-between">
              <h4 className="mb-0 font-size-18">Total Bets</h4>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => { fetchBets(); fetchVoided() }}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {notice && (
          <div className="row">
            <div className="col-12">
              <div className={`alert py-2 ${notice.ok ? 'alert-success' : 'alert-danger'}`}>
                {notice.message}
              </div>
            </div>
          </div>
        )}

        <div className="row mb-3">
          <div className="col-md-8">
            <div className="input-group input-group-sm">
              <input
                type="text"
                className="form-control"
                placeholder={tab === 'settled' ? 'Search by match, market, selection...' : 'Filter this page by username, match, selection...'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') runSearch() }}
              />
              <button type="button" className="btn btn-primary" onClick={runSearch}>
                Search
              </button>
              {query && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => { setSearch(''); setQuery(''); setPage(1) }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="row mb-2">
          <div className="col-12">
            <div className="btn-group btn-group-sm" role="group">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  className={`btn ${category === c.key ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => switchCategory(c.key)}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <ul className="nav nav-tabs mb-3">
              {TABS.map((t) => (
                <li className="nav-item" key={t.key}>
                  <button
                    type="button"
                    className={`nav-link ${tab === t.key ? 'active' : ''}`}
                    onClick={() => switchTab(t.key)}
                  >
                    {t.label}{tab === t.key ? ` (${total})` : ''}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {error && (
          <div className="row">
            <div className="col-12">
              <div className="alert alert-danger py-2">{error}</div>
            </div>
          </div>
        )}

        <div className="row">
          <div className="col-12">
            <div className="table-responsive">
              <table className="table table-bordered table-sm mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Bet ID</th>
                    <th>User</th>
                    <th>{isCasino ? 'Game' : 'Match'}</th>
                    <th>Selection</th>
                    <th className="text-center">Type</th>
                    <th className="text-end">Odds</th>
                    <th className="text-end">Stake</th>
                    <th className="text-center">Status</th>
                    <th>Placed At</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={10} className="text-center py-4">Loading...</td></tr>
                  ) : visibleBets.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center py-4">
                        No {tab} bets{query ? ` for "${query}"` : ''}
                      </td>
                    </tr>
                  ) : (
                    visibleBets.map((b) => {
                      const st = statusOf(b)
                      const locked = isVoided(b)
                      return (
                        <tr key={`${b.source}-${b.id}`}>
                          <td>#{b.id}</td>
                          <td>
                            <div className="fw-bold">{b.username || '-'}</div>
                            <small className="text-muted">#{b.user_id}</small>
                          </td>
                          <td>
                            <div>{b.description || '-'}</div>
                            <small className="text-muted">{b.game_type}</small>
                          </td>
                          <td>{b.selection || '-'}</td>
                          <td className="text-center">
                            {b.bet_type ? (
                              <span className={`badge px-2 ${String(b.bet_type).toLowerCase() === 'lay' ? 'bg-danger' : 'bg-primary'}`}>
                                {String(b.bet_type).toUpperCase()}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="text-end">{b.odds ?? '-'}</td>
                          <td className="text-end">{fmt(b.amount)}</td>
                          <td className="text-center">
                            <span className={`badge px-2 ${st.cls}`}>{st.label}</span>
                          </td>
                          <td><small>{fmtDate(b.created_at)}</small></td>
                          <td className="text-center">
                            {locked ? (
                              <button type="button" className="btn btn-secondary btn-sm" disabled>
                                Already Voided
                              </button>
                            ) : (
                              <div className="d-inline-flex gap-1">
                                {(isCasino || tab === 'unsettled') && (
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => openConfirm('void', b)}
                                  >
                                    Void Bet
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="btn btn-dark btn-sm"
                                  onClick={() => openConfirm('delete', b)}
                                >
                                  Delete Bet
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                <small className="text-muted">
                  Showing {from}–{to} of {total}
                </small>
                <div className="btn-group btn-group-sm">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={page === 1 || loading}
                    onClick={() => setPage(1)}
                  >
                    «
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={page === 1 || loading}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Prev
                  </button>
                  {pageNumbers().map((n, i, arr) => (
                    <button
                      key={n}
                      type="button"
                      className={`btn ${n === page ? 'btn-primary' : 'btn-outline-secondary'}`}
                      disabled={loading}
                      onClick={() => setPage(n)}
                    >
                      {i > 0 && arr[i - 1] !== n - 1 ? `… ${n}` : n}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage(totalPages)}
                  >
                    »
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <SettlementVoidModal
        show={!!confirm}
        title={confirm?.type === 'void' ? 'Void Bet' : 'Delete Bet'}
        confirmLabel={confirm?.type === 'void' ? 'Confirm Void' : 'Confirm Delete'}
        onHide={() => { if (!submitting) setConfirm(null) }}
        onConfirm={doAction}
        submitting={submitting}
        requirePassword
        password={txnPassword}
        onPasswordChange={setTxnPassword}
        error={actionError}
      >
        {confirm && (
          <div>
            <p className="mb-2">
              {isCasino
                ? (confirm.type === 'delete'
                  ? (tab === 'settled'
                    ? 'Ye settled casino bet delete hogi — paisa ulta hoga (net wapas) aur bet + statement + point rows hat jayengi. 24 ghante se purani bet delete nahi hoti.'
                    : 'Ye open casino bet delete hogi — locked stake wapas jayega aur bet ka record hat jayega.')
                  : tab === 'settled'
                    ? 'Ye settled casino bet void hogi — cash + inr_balance dono ulte honge (net wapas), ledger ki ulti entry aur all_point chain reverse hogi. 24 ghante se purani bet void nahi hoti.'
                    : 'Ye open casino bet void hogi — locked stake user ko wapas chala jayega.')
                : confirm.type === 'void'
                ? 'Ye open bet void hogi — stake user ko wapas chala jayega.'
                : tab === 'settled'
                  ? 'Ye settled bet delete hogi — paisa ulta hoga (net wapas) aur bet + statement rows hat jayengi. 24 ghante se purani bet delete nahi hoti.'
                  : 'Ye open bet delete hogi — stake wapas jayega aur bet ka record hat jayega.'}
            </p>
            <table className="table table-sm mb-0">
              <tbody>
                <tr><th>Bet</th><td>#{confirm.bet.id}</td></tr>
                <tr><th>User</th><td>{confirm.bet.username}</td></tr>
                <tr><th>{isCasino ? 'Game' : 'Match'}</th><td>{confirm.bet.description}</td></tr>
                <tr><th>Selection</th><td>{confirm.bet.selection}</td></tr>
                <tr><th>Stake</th><td>{fmt(confirm.bet.amount)}</td></tr>
              </tbody>
            </table>
          </div>
        )}
      </SettlementVoidModal>
    </Layout>
  )
}
